import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';

import type { FieldReportSyncVM, FieldReportBlockFieldVM } from '@acceligentllc/shared/dtos/socket/view/newFieldReport/base';

import type FieldReportVM from 'ab-viewModels/fieldReport/fieldReport.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';
import type { BlockValueType, ValueType } from '@acceligentllc/shared/utils/fieldReport';
import { shouldReportForAccountBeReadOnly } from '@acceligentllc/shared/utils/workOrderFieldReport';

import { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import { FieldReportBlockType } from '@acceligentllc/shared/enums/fieldReportBlockType';
import type { WorkOrderReviewStatus, WorkOrderReviewLevel } from '@acceligentllc/shared/enums/workOrderReviewStatus';
import type { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { FIELD_REPORT } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import LoadingOverlay from 'af-components/LoadingOverlay';

import * as FieldReportActions from 'af-actions/fieldReport';

import type { FieldWithIndex } from '..';
import { fieldReportFieldHasValue } from '../helpers';
import Instance from './Instance';
import TotalBlock from './TotalBlock';
import type { RequestQueue } from '../../helpers';

interface OwnProps {
	areFRsReadOnly: boolean;
	fieldReportId: number;
	fieldReportTypeId: number;
	highlightedBlockId: Nullable<string>;
	change: (fieldName: string, value: BlockValueType) => void;
	lock: (blockId: string) => void;
	unlock: () => void;
	forceUnlock: (blockId: string) => void;
	isPreview: boolean;
	focusedBlockId: Nullable<number>;
	setFieldToFocus: (_ref: HTMLDivElement) => void;
	removeFocusedField: () => void;
	isAbleToReject: boolean;
	isPaused: boolean;
	isVirtual: boolean;
	fieldReportAccessRole: Nullable<FieldReportAccessRole>;
	workOrderStatus: WorkOrderStatus;
	reviewStatus: WorkOrderReviewStatus;
	reviewLevel: WorkOrderReviewLevel;
	requestQueue: RequestQueue;
	allocatedFieldsWithIndex: Nullable<FieldWithIndex[]>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const segmentHasAnyValue = (
	segmentId: string,
	fieldReport: FieldReportVM & FieldReportSyncVM,
	fieldReportBlockFieldsByBlockId: Record<string, (FieldReportBlockFieldVM & FieldReportBlockFieldVM)[]>,
	formState: Record<string, ValueType>
) => {
	const seg = fieldReport.segmentMap[segmentId];
	if (!seg) {
		return false;
	}

	for (const blockId of seg.blocks) {
		const frbfs = fieldReportBlockFieldsByBlockId[blockId] ?? [];

		for (const frbf of frbfs) {
			const def = fieldReport.fieldMap[frbf.reportBlockFieldId];
			if (fieldReportFieldHasValue(formState[`field-${frbf.id}`], def)) {
				return true;
			}
		}
	}

	return false;
};

const FieldReportType: React.FC<Props> = (props) => {
	const {
		addInstance,
		areFRsReadOnly,
		fieldReportId,
		fieldReportTypeId,
		highlightedBlockId,
		change,
		lock,
		unlock,
		forceUnlock,
		isPreview,
		focusedBlockId,
		setFieldToFocus,
		removeFocusedField,
		isAbleToReject,
		requestQueue,
		allocatedFieldsWithIndex,
		fieldReportType,
		upperTotalBlockId,
		lowerTotalBlockId,
		firstBlockName,
		showAddInstance,
		fieldReport,
		formState,
	} = props;

	const [addingBlock, setAddingBlock] = React.useState(false);

	// componentWillUnmount
	React.useEffect(() => {
		return () => {
			if (focusedBlockId) {
				removeFocusedField();
			}
		};
	}, [focusedBlockId, removeFocusedField]);

	const handleAddInstance = React.useCallback(() => {
		setAddingBlock(true);
	}, []);

	const handleAddBlock = React.useCallback(async (isCancelled: () => boolean) => {
		try {
			if (isNaN(fieldReportId)) {
				return;
			}
			await addInstance(fieldReportId, fieldReportTypeId);
		} finally {
			if (!isCancelled()) {
				setAddingBlock(false);
			}
		}
	}, [addInstance, fieldReportId, fieldReportTypeId]);

	React.useEffect(() => {
		if (!addingBlock) {
			return;
		}

		let cancelled = false;
		handleAddBlock(() => cancelled);

		return () => { cancelled = true; };
	}, [addingBlock, handleAddBlock]);

	const { allocatedBlockIds, allocatedInstances } = React.useMemo(() => {
		if (!fieldReport) {
			return { allocatedBlockIds: null as Nullable<string[]>, allocatedInstances: null as Nullable<string[]> };
		}

		const allocatedFieldIds = (allocatedFieldsWithIndex ?? []).map((f) => f.fieldReportBlockFieldId);
		if (!allocatedFieldIds.length) {
			return { allocatedBlockIds: null as Nullable<string[]>, allocatedInstances: null as Nullable<string[]> };
		}

		const _allocatedBlockIds = filterMap(
			Object.values(fieldReport.fieldReportBlockFieldMap),
			(block) => allocatedFieldIds.includes(+block.id),
			(block) => block.blockId
		);

		const allocatedSegments = filterMap(
			Object.values(fieldReport.segmentMap),
			(segment) => segment.blocks.some((blockId) => _allocatedBlockIds.includes(blockId)),
			(segment) => segment.id
		);

		const _allocatedInstances = filterMap(
			Object.values(fieldReport.instanceMap),
			(instance) => instance.segments.some((segmentId) => allocatedSegments.includes(segmentId)),
			(instance) => instance.id
		);

		return { allocatedBlockIds: _allocatedBlockIds, allocatedInstances: _allocatedInstances };
	}, [fieldReport, allocatedFieldsWithIndex]);

	const { lastInstanceIsEmpty, emptySegmentIds } = React.useMemo(() => {
		if (!fieldReportType || !fieldReport || !formState) {
			return { lastInstanceIsEmpty: true, emptySegmentIds: [] as string[] };
		}

		// ----------------------------
		// 1) Build an index: blockId -> fieldReportBlockFields[]
		// ----------------------------
		const fieldReportBlockFieldsByBlockId = Object.values(fieldReport.fieldReportBlockFieldMap)
			.reduce<Record<string, typeof fieldReport.fieldReportBlockFieldMap[string][]>>(
				(acc, frbf) => {
					const blockId = frbf.blockId;

					if (!acc[blockId]) {
						acc[blockId] = [];
					}
					acc[blockId].push(frbf);

					return acc;
				},
				{}
			);

		// ----------------------------
		// 2) Last instance empty?
		// ----------------------------
		const lastInstanceId = fieldReportType.instances[fieldReportType.instances.length - 1];
		const lastInstance = fieldReport.instanceMap[lastInstanceId];

		if (!lastInstance) {
			return { lastInstanceIsEmpty: true, emptySegmentIds: [] as string[] };
		}

		const lastInstanceIsEmptyResolved = !lastInstance.segments.some((segmentId) => {
			return segmentHasAnyValue(segmentId, fieldReport, fieldReportBlockFieldsByBlockId, formState);
		});

		// ----------------------------
		// 3) Empty segments across whole report type
		// ----------------------------
		const emptySegmentIdsResolved = fieldReportType.instances
			.flatMap((instanceId) => {
				const inst = fieldReport.instanceMap[instanceId];
				return inst ? inst.segments : [];
			})
			.filter((segmentId) => {
				return !segmentHasAnyValue(segmentId, fieldReport, fieldReportBlockFieldsByBlockId, formState);
			});

		return {
			lastInstanceIsEmpty: lastInstanceIsEmptyResolved,
			emptySegmentIds: emptySegmentIdsResolved,
		};
	}, [fieldReportType, fieldReport, formState]);

	const renderTotalBlock = React.useCallback((type: FieldReportBlockType) => {
		const blockId = type === FieldReportBlockType.UPPER_TOTAL ? upperTotalBlockId : lowerTotalBlockId;

		if (!blockId || (allocatedBlockIds && !allocatedBlockIds.includes(blockId))) {
			return null;
		}

		return (
			<TotalBlock
				allocatedFieldsWithIndex={allocatedFieldsWithIndex}
				blockId={blockId}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				focusedBlockId={focusedBlockId}
				key={blockId}
				removeFocusedField={removeFocusedField}
				setFieldToFocus={setFieldToFocus}
			/>
		);
	}, [
		allocatedBlockIds,
		allocatedFieldsWithIndex,
		fieldReportId,
		fieldReportTypeId,
		focusedBlockId,
		lowerTotalBlockId,
		removeFocusedField,
		setFieldToFocus,
		upperTotalBlockId,
	]);

	const renderInstance = React.useCallback((instanceId: string, key: number) => {
		if (!fieldReportType || (allocatedInstances && !allocatedInstances.includes(instanceId))) {
			return null;
		}

		return (
			<Instance
				allocatedFieldsWithIndex={allocatedFieldsWithIndex}
				areFRsReadOnly={areFRsReadOnly}
				change={change}
				emptySegmentIds={emptySegmentIds}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				focusedBlockId={focusedBlockId}
				forceUnlock={forceUnlock}
				highlightedBlockId={highlightedBlockId}
				instanceId={instanceId}
				isAbleToReject={isAbleToReject}
				isPreview={isPreview}
				key={key}
				lock={lock}
				removeFocusedField={removeFocusedField}
				requestQueue={requestQueue}
				setFieldToFocus={setFieldToFocus}
				showInstanceCount={fieldReportType.instances.length > 1}
				unlock={unlock}
			/>
		);
	}, [
		allocatedFieldsWithIndex,
		allocatedInstances,
		areFRsReadOnly,
		change,
		emptySegmentIds,
		fieldReportId,
		fieldReportType,
		fieldReportTypeId,
		focusedBlockId,
		forceUnlock,
		highlightedBlockId,
		isAbleToReject,
		isPreview,
		lock,
		removeFocusedField,
		requestQueue,
		setFieldToFocus,
		unlock,
	]);

	const renderAddInstance = React.useCallback(() => {
		return (
			<div className={bemElement('field-report-type', 'add-instance-button-container')}>
				<Button
					disabled={lastInstanceIsEmpty}
					icon="ActionPlusSmall"
					label={`Add ${firstBlockName}`}
					onClick={handleAddInstance}
					style="primary"
				/>
			</div>
		);
	}, [firstBlockName, handleAddInstance, lastInstanceIsEmpty]);

	if (!fieldReportType) {
		return null;
	}

	return (
		<>
			{renderTotalBlock(FieldReportBlockType.UPPER_TOTAL)}
			{fieldReportType.instances.map(renderInstance)}
			{showAddInstance && !isPreview && renderAddInstance()}
			{renderTotalBlock(FieldReportBlockType.LOWER_TOTAL)}
			<LoadingOverlay label="Adding Block" show={addingBlock} />
		</>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { fieldReport: { fieldReport }, user: { userData, companyData } } = state;
	const {
		fieldReportTypeId,
		isPaused,
		isPreview,
		reviewLevel,
		reviewStatus,
		workOrderStatus,
		fieldReportAccessRole,
		isVirtual,
	} = ownProps;

	const fieldReportType = fieldReport?.typeMap?.[fieldReportTypeId] ?? null;

	if (!fieldReportType || !fieldReport) {
		return {
			fieldReport: null,
			fieldReportType: null,
			formState: null,
			upperTotalBlockId: null,
			lowerTotalBlockId: null,
			firstBlockName: null,
			showAddInstance: false,
			isPreview: true,
		};
	}

	const formState = getFormValues(FIELD_REPORT)(state);

	const upperTotalBlockId = fieldReportType.upperTotalBlockId;
	const lowerTotalBlockId = fieldReportType.lowerTotalBlockId;

	const instanceId = fieldReportType.instances[0];
	const instance = fieldReport.instanceMap[instanceId];
	const segment = instance ? fieldReport.segmentMap[instance.segments[0]] : null;
	const fieldBlock = segment ? fieldReport.fieldReportBlockMap[segment.blocks[0]] : null;
	const block = fieldBlock ? fieldReport.blockMap[fieldBlock.reportBlockId] : null;

	const { isCompanyAdmin, permissions } = companyData!;
	const { role } = userData!;

	const isAccountingOrAdmin = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.ACCOUNTING, permissions, isCompanyAdmin, role);
	const isManagement = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.MANAGEMENT, permissions, isCompanyAdmin, role);
	const hasPermissionToEdit = isAccountingOrAdmin || isAllowed(PagePermissions.COMPANY.FIELD_REPORT.FILL, permissions, isCompanyAdmin, role);
	const canEditInFinalizing = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.FINALIZING, permissions, isCompanyAdmin, role);

	const resolvedIsPreview = (fieldReportType.fwCanEditAfterSubmit && !isPaused && !isVirtual)
		? shouldReportForAccountBeReadOnly(
			reviewStatus,
			reviewLevel,
			workOrderStatus,
			fieldReportType.fwCanEditAfterSubmit,
			isManagement ? FieldReportAccessRole.MANAGEMENT : fieldReportAccessRole,
			hasPermissionToEdit,
			isAccountingOrAdmin,
			canEditInFinalizing
		)
		: isPreview;

	return {
		fieldReport,
		fieldReportType,
		formState,
		upperTotalBlockId,
		lowerTotalBlockId,
		firstBlockName: block?.name,
		showAddInstance: fieldReportType?.isRepeatable,
		isPreview: resolvedIsPreview,
	};
}
function mapDispatchToProps() {
	return {
		addInstance: FieldReportActions.addInstance,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(FieldReportType);
